import * as React from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { Link } from 'gatsby';
import { formatDateToString } from 'utils/formatDateToString';
import { classnames } from 'helpers/utils';
import * as styles from './card.module.scss';

export enum CardType {
  aboutUs = 'aboutUs',
  author = 'author',
  reviewer = 'reviewer',
  mixed = 'mixed', // always has an author, possibly a reviewer
}

interface CardProps {
  image: IGatsbyImageData | undefined;
  fullName: string;
  principalSlug: string;
  type: CardType;
  description?: string;
  shortBio?: string; // used for CardType.author & CardType.reviewer
  reviewer?: string;
  secondarySlug?: string;
  medicalReviewDate?: string;
  className?: string;
  sources?: boolean;
}

export const Card: React.FC<CardProps> = ({
  image, fullName, description, shortBio, principalSlug, secondarySlug, type,
  reviewer, medicalReviewDate, className, sources = false,
}) => {
  const formattedDate = formatDateToString(medicalReviewDate || '');
  return (
    <div className={classnames(styles.card, className || '')}>
      {image
        && (
          <GatsbyImage
            image={image}
            alt={fullName}
            className={type === CardType.aboutUs ? styles.cardImageAboutUs : styles.cardImage}
          />
        )}
      {type === CardType.aboutUs && (
        <div className={styles.cardContent}>
          <h2 className={styles.cardTitle}>{fullName}</h2>
          <span className={styles.cardDescription}>{description}</span>
          <Link to={principalSlug} className={styles.cardReadMore}>
            Read more&gt;
          </Link>
        </div>
      )}
      {(type === CardType.mixed) && (
        <div className={styles.content}>
          <span className={styles.writtenBySection}>
            <span>Written by</span>
            <Link to={principalSlug}>{fullName}</Link>
          </span>
          {reviewer && secondarySlug && (
            <div className={styles.reviewdBySection}>
              <div>
                <span className={styles.reviewedOn}>
                  Medical Reviewed
                  {medicalReviewDate && ` on ${formattedDate}`}
                </span>
              </div>
              <span className={styles.reviewedBy}>
                {' by'}
                <Link to={secondarySlug}>{reviewer}</Link>
              </span>
            </div>
          )}
          {sources && (
            <span className={styles.evidenceBased}>🔬 Evidence Based</span>
          )}
        </div>
      )}
      {(type === CardType.author || type === CardType.reviewer) && (
        <div className={styles.content}>
          <span className={styles.singleTypeContent}>
            <span>{type === CardType.reviewer ? 'Medical Reviewed by' : 'Written by'}</span>
            <Link to={principalSlug}>{fullName}</Link>
          </span>
          {shortBio && (
            <span className={styles.shortBio}>
              {shortBio}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
